/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useEffect, useMemo, useRef } from 'react';
import { useBasket } from '@/providers/basket';
import { useCheckoutActions } from '@/hooks/use-checkout-actions';
import { getDeliveryShipment } from '@/extensions/bopis/lib/basket-utils';
import { getBasketBoolean, getShipmentString, normaliseDeliveryWindows } from '@/lib/checkout/ohds-basket-utils';

/**
 * Minimal test seam — the slice of `useCheckoutActions` this hook consumes.
 * Letting callers inject a stub keeps the hook testable without standing up
 * the full checkout-actions provider tree (parallel to the picker's
 * `HdMethodCardDatePickerActions` shape; the two are structurally identical
 * so callers can pass either).
 */
export interface UseAutoPopulateDeliveryScheduleActions {
    submitDeliverySchedule: (selectedDate: string | null, options?: { isUserSelected?: boolean }) => void;
    deliveryScheduleFetcher: { state: 'idle' | 'loading' | 'submitting'; data?: { success?: boolean } | null };
}

export interface UseAutoPopulateDeliveryScheduleOptions {
    /**
     * Master gate. When `false`, the hook is a no-op (used by Storybook
     * stories and tests that need to mount a consumer without firing a
     * basket PATCH). Defaults to `true`.
     */
    enabled?: boolean;
    /**
     * Test/Storybook seam: lets stories run without standing up
     * `<BasketProvider/>`. Production callers leave this unset so the live
     * `useBasket()` is used.
     */
    useBasketHook?: typeof useBasket;
    /**
     * Test/Storybook seam: stub for the checkout-actions slice. Default
     * uses the real `useCheckoutActions` hook so production callers do
     * not need to wire anything.
     */
    useActions?: () => UseAutoPopulateDeliveryScheduleActions;
}

/**
 * INT-12 FE-03 — fire-once auto-populate of `c_requestedDeliveryDatetime`
 * on the first render where the basket has an HD delivery shipment with
 * scheduling windows but no shopper-selected date yet. PATCHes the basket
 * with `isUserSelected=false` so the `afterPATCH` hook stamps
 * `c_isUserRequestedDeliveryDatetime=false`, preserving the SYSTEM-fallback
 * contract that `order.afterPOST` relies on to write
 * `onlineDeliveryScheduledBy="System"`.
 *
 * Gating conditions (ALL must hold for the PATCH to fire):
 *   • `enabled` (caller gate; default `true`)
 *   • `basket.c_ohdsAvailable === true` — the basket's CURRENT shipping
 *      ZIP supports OHDS AND the master kill switch is on. Fresh-per-
 *      response signal (recomputed by `enrichBasketResponseHDShipping`
 *      via `ZipMapUtil.ohdsZipMapCheck`), so it tracks the live ZIP
 *      regardless of whether the `shipping_address.afterPUT` recompute
 *      hook is healthy on a given instance. Strictly stronger than the
 *      old `c_ohdsEnabled`-only check, which let auto-populate fire on
 *      ineligible ZIPs whenever stale `c_deliveryWindows` survived.
 *   • The delivery shipment has at least one entry in `c_deliveryWindows`
 *   • The delivery shipment has NO existing `c_requestedDeliveryDatetime`
 *   • The fetcher is not currently submitting (re-entrancy guard)
 *
 * Per-mount idempotency: a `useRef` ensures we never re-fire on benign
 * dep fluctuations (fetcher state flipping idle → submitting → idle, or
 * a PATCH-failure render where `persistedDate` stays `null`). Across
 * mounts, the basket-side `persistedDate` check is the de-dup — once
 * the PATCH lands, every subsequent mount sees a non-null date and
 * short-circuits.
 *
 * The latch is RESET (per-mount) when either teardown transition fires:
 *   1. Windows go empty (`availableDates.length === 0`) — covers
 *      ineligible ZIP, master switch off, and service failure.
 *   2. `persistedDate` transitions from set → null — covers BE-02 in
 *      `applyDeliveryWindowsToShipment` clearing the prior date during
 *      a same-mount ZIP change to a different eligible region whose
 *      windows don't include the previously-committed date.
 * Both signal that the auto-populate we previously committed to no
 * longer holds. Without these resets, a shopper who edits the address
 * from a non-OHDS ZIP back to an eligible one (or from one eligible
 * region to another) would see the row render with no date and the
 * picker would never auto-populate until the next page remount.
 *
 * This hook deliberately renders nothing. It is intended to be called
 * once from the highest stable surface that owns the HD delivery UX
 * (today: `DeliveryMethodToggle`; pre-PR #114: `HdMethodCardDatePicker`).
 * Calling it from multiple sibling components on the same basket is safe
 * (basket-side de-dup handles it) but wasteful.
 *
 * @example
 * function DeliveryMethodToggle() {
 *     useAutoPopulateDeliverySchedule();
 *     return <RadioGroup>...</RadioGroup>;
 * }
 */
export function useAutoPopulateDeliverySchedule(options: UseAutoPopulateDeliveryScheduleOptions = {}): void {
    const { enabled = true, useBasketHook = useBasket, useActions = useCheckoutActions } = options;

    const basket = useBasketHook();
    const { submitDeliverySchedule, deliveryScheduleFetcher } = useActions();

    const deliveryShipment = getDeliveryShipment(basket ?? undefined);
    const persistedDate = getShipmentString(deliveryShipment, 'c_requestedDeliveryDatetime');
    const rawWindows = (deliveryShipment as unknown as Record<string, unknown> | undefined)?.c_deliveryWindows;
    const availableDates = useMemo(() => normaliseDeliveryWindows(rawWindows).sort(), [rawWindows]);
    const ohdsAvailable = getBasketBoolean(basket, 'c_ohdsAvailable');

    const autoPopulatedRef = useRef(false);
    const prevPersistedDateRef = useRef<string | null>(null);
    useEffect(() => {
        // Reset the per-mount idempotency latch on teardown transitions
        // so a re-entry into the "windows + no date" state can re-fire
        // (see the hook's JSDoc above for the two transitions). The prev
        // ref is updated unconditionally — even on bailing renders — so
        // we observe every persistedDate value, not just the ones that
        // pass the gates below.
        const prevPersistedDate = prevPersistedDateRef.current;
        prevPersistedDateRef.current = persistedDate;
        if (availableDates.length === 0 || (prevPersistedDate !== null && persistedDate === null)) {
            autoPopulatedRef.current = false;
        }

        if (!enabled) return;
        if (autoPopulatedRef.current) return;
        if (!ohdsAvailable) return;
        if (persistedDate) return;
        if (availableDates.length === 0) return;
        if (deliveryScheduleFetcher.state === 'submitting') return;

        autoPopulatedRef.current = true;
        submitDeliverySchedule(availableDates[0], { isUserSelected: false });
    }, [enabled, ohdsAvailable, persistedDate, availableDates, deliveryScheduleFetcher.state, submitDeliverySchedule]);
}
